import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiV2 from '../../../services/apiV2';
import { ClassroomStudents } from '../../../components/v2/teacher/ClassroomStudents';
import { TeacherLiveDashboardPanel } from '../../../components/v2/teacher/TeacherLiveDashboardPanel';
import { ActivityOffers } from '../../../components/v2/teacher/ActivityOffers';

function LiveActivitySignals({runId}: {runId: number}) {
  const [dashboard, setDashboard] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [bucket, setBucket] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const response = await apiV2.get(`/lesson-runs/${runId}/live-dashboard/`);
        if (live) { setDashboard(response.data); setError(null); }
      } catch { if (live) setError('Live signals are temporarily unavailable.'); }
      finally { if (live) timer = setTimeout(refresh, 5000); }
    };
    void refresh();
    return () => { live = false; clearTimeout(timer); };
  }, [runId]);
  return <TeacherLiveDashboardPanel dashboard={dashboard} loading={!dashboard && !error} error={error} selectedBucket={bucket} onSelectBucket={setBucket} />;
}

/** Teacher-only, class-pinned screen. Never included in the projector view. */
export function StudentMonitor() {
  const { sectionId } = useParams();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const response = await apiV2.get(`/pathways/sections/${sectionId}/classroom/`);
        if (live) { setData(response.data); setError(false); }
      } catch { if (live) setError(true); }
      finally { if (live) timer = setTimeout(refresh, 5000); }
    };
    void refresh();
    return () => { live = false; clearTimeout(timer); };
  }, [sectionId]);
  return <main className="activity-teaching-screen min-h-screen bg-surface-alt p-6"><div className="mx-auto max-w-6xl space-y-5">
    <header className="flex flex-wrap items-center justify-between gap-4"><div><p className="text-sm font-semibold text-brand">TEACHER ONLY · DO NOT PROJECT</p><h1 className="text-3xl font-semibold">Student monitor</h1><p className="text-muted">This window stays with the class as activities change.</p></div><Link className="btn-secondary" to={`/v2/teacher/teach?section=${sectionId}`}>Back to Teach</Link></header>
    {error && <p role="alert">Connection interrupted. Displayed information may be out of date; reconnecting…</p>}
    {!data ? <p role="status">Loading student work…</p> : <>
      <section className="rounded-xl border border-border bg-surface p-5"><h2 className="text-xl font-semibold">{data.active_run?.title || 'Independent work time'}</h2>{data.active_run && <Link className="mt-3 inline-block text-brand underline" to={`/v2/teacher/run/${data.active_run.id}/ops`} target="_blank" rel="noopener noreferrer">Open live responses & support tools</Link>}<p className="mt-2 text-sm text-muted">Offline lab work may not generate responses. These signals are not engagement scores.</p></section>
      <section className="rounded-xl border border-border bg-surface p-5"><h2 className="text-xl font-semibold">Today’s independent activities</h2>{(data.agenda || []).filter((item: any) => item.assignment_id && item.status !== 'skipped').map((item: any) => <details key={item.id} className="border-b border-border py-3"><summary className="cursor-pointer">{item.title} · {(item.progress || []).filter((p: any) => p.status === 'completed').length}/{(item.progress || []).length} finished</summary><ul className="mt-3 space-y-2">{(item.progress || []).map((p: any) => <li key={p.student_id}><Link className="text-brand underline" target="_blank" rel="noopener noreferrer" to={`/v2/teacher/assignments/${item.assignment_id}/students/${p.student_id}`}>{data.students.find((s: any) => s.id === p.student_id)?.name || 'Student'}</Link> · {p.status.replace(/_/g, ' ')}</li>)}</ul></details>)}<p className="mt-3 text-sm text-muted">Use Add an activity in Teach to offer extra practice, support, or a challenge to selected students.</p></section>
      {data.active_run && <LiveActivitySignals key={data.active_run.id} runId={data.active_run.id} />}
      <ActivityOffers key={sectionId} sectionId={Number(sectionId)} />
      <ClassroomStudents students={data.students || []} />
    </>}
  </div></main>;
}
